import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { verifyTopup, verifyRiderTopup, verifySubscription, verifyOrderPayment, verifyInvoicePayment } from "../api/client";

/**
 * Landing page Paystack/Flutterwave redirect the browser to after a hosted
 * checkout completes. Reads the reference back out of the query string,
 * figures out which flow it belongs to from its prefix, and calls the
 * matching verify endpoint. One page serves wallet top-up, subscriptions,
 * and order payment since all three otherwise repeat this exact dance.
 */
export default function PaymentCallback() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "failed"
  const [message, setMessage] = useState("Confirming your payment...");
  const [continueTo, setContinueTo] = useState("/dashboard");
  const [continueLabel, setContinueLabel] = useState("Back to Dashboard");

  useEffect(() => {
    async function run() {
      // Paystack sends reference/trxref; Flutterwave sends tx_ref + status.
      const reference = params.get("reference") || params.get("trxref") || params.get("tx_ref");
      const gateway = params.get("trxref") ? "paystack" : params.get("tx_ref") ? "flutterwave" : "paystack";
      const flwStatus = params.get("status"); // "successful" | "completed" | "cancelled" | "failed" (Flutterwave)
      const transactionId = params.get("transaction_id");

      if (!reference) {
        setStatus("failed");
        setMessage("No payment reference was returned — the payment may have been cancelled.");
        return;
      }
      // Flutterwave redirects with status=completed or status=successful on payment completion
      const isFlwSuccess = flwStatus && ["successful", "completed"].includes(flwStatus.toLowerCase());
      if (flwStatus && !isFlwSuccess) {
        setStatus("failed");
        setMessage("Payment was not completed.");
        return;
      }

      try {
        if (reference.startsWith("topup_")) {
          await verifyTopup(reference, gateway, undefined, transactionId);
          setStatus("success");
          setMessage("Your wallet has been funded successfully!");
          setContinueTo("/wallet");
          setContinueLabel("Go to Wallet");
        } else if (reference.startsWith("rider_topup_")) {
          await verifyRiderTopup(reference, gateway, transactionId);
          setStatus("success");
          setMessage("Dispatcher wallet has been topped up successfully!");
          setContinueTo("/rider/wallet");
          setContinueLabel("Back to Dispatcher Wallet");
        } else if (reference.startsWith("SUB_")) {
          const plan = reference.split("_")[1] || "plus";
          await verifySubscription(reference, gateway, plan);
          setStatus("success");
          setMessage("Your subscription is now active!");
          setContinueTo("/subscription");
          setContinueLabel("Go to Subscription");
        } else if (reference.startsWith("order_")) {
          const orderId = reference.split("_")[1];
          await verifyOrderPayment(orderId, reference, gateway);
          setStatus("success");
          setMessage("Payment received — your delivery is confirmed!");
          setContinueTo(`/track/${orderId}`);
          setContinueLabel("Track Delivery");
        } else if (reference.startsWith("INV_")) {
          const invoiceId = reference.split("_")[1];
          await verifyInvoicePayment(invoiceId, reference, gateway);
          setStatus("success");
          setMessage("Invoice payment received — thank you!");
          setContinueTo("/b2b");
          setContinueLabel("Back to Business Portal");
        } else {
          setStatus("failed");
          setMessage("Unrecognized payment reference.");
        }
      } catch (err) {
        setStatus("failed");
        setMessage(err.response?.data?.error || "We couldn't confirm this payment. If you were charged, contact support.");
      }
    }
    run();
  }, [params]);

  return (
    <div className="page" style={{ textAlign: "center" }}>
      <div className="brand-header">
        <span className="brand-logo">{status === "verifying" ? "⏳" : status === "success" ? "✅" : "⚠️"}</span>
        <span className="brand-name">
          {status === "verifying" ? "Confirming Payment" : status === "success" ? "Payment Successful" : "Payment Issue"}
        </span>
      </div>
      <p className={status === "failed" ? "error" : "hint"} style={{ fontSize: "15px" }}>{message}</p>
      {status !== "verifying" && (
        <Link to={continueTo}>
          <button style={{ marginTop: "16px" }}>{continueLabel}</button>
        </Link>
      )}
    </div>
  );
}
